import { useEffect, useMemo, useRef } from 'react';

import {
  useLazyCustomFields,
  useRequiredAndVisibleCustomFields,
} from 'utils/customFields';
import { useCurrentUserInAcw } from 'utils/user';

type UseConversationStateOptions = {
  /** The conversation id */
  readonly csid: number;
};

/**
 * A hook for determining whether a conversation assigned to current agent has
 * missing required custom fields when the agent is in after conversation work
 * (ACW) for said conversation.
 */
export const useConversationHasMissingRequiredCustomFieldsInAcw = ({
  csid,
}: UseConversationStateOptions): boolean => {
  const hasMissingRequiredVisibleCustomFieldsInAcw = useRef(false);
  const isInAcw = useCurrentUserInAcw({ csid });

  const [readCustomFields, { asyncStatus, customFields }] =
    useLazyCustomFields();

  const requiredAndVisibleCustomFields =
    useRequiredAndVisibleCustomFields(customFields);

  // Handles reading custom fields.
  useEffect(() => {
    if (isInAcw) {
      readCustomFields({
        entityId: 'conversation',
        entityInstanceId: `${csid}`,
      });
    }
  }, [csid, isInAcw, readCustomFields]);

  const emptyReqAndVisibleCustomFields = useMemo(() => {
    return requiredAndVisibleCustomFields;
  }, [requiredAndVisibleCustomFields]);

  return useMemo(() => {
    if (!isInAcw) {
      return false;
    }

    if (asyncStatus !== 'fulfilled') {
      return hasMissingRequiredVisibleCustomFieldsInAcw.current;
    }

    hasMissingRequiredVisibleCustomFieldsInAcw.current =
      !!emptyReqAndVisibleCustomFields.length;

    return hasMissingRequiredVisibleCustomFieldsInAcw.current;
  }, [isInAcw, asyncStatus, emptyReqAndVisibleCustomFields.length]);
};
